import { CodeMapCategory, CodeMapTokenWithLocate } from 'adminConsole/page'
import { Formik, FormikProps } from 'formik'
import { CodeMapDisplay } from 'mobileApp/common/component/codeMap/CodeMapDisplay'
import { CodeMapRadio } from 'mobileApp/common/component/codeMap/CodeMapRadio'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useGetCodeMapCategorySystemValue } from 'share/util/common'
import { getOtpMethod, maskSensitive } from 'share/util/helper'
import toastr from 'toastr'
import { OtpVerifyMethod } from '../model/otp'
import { CountdownButton } from './CountdownButton'
import { ModalWithReact } from './ModalWithReact'
import { OtpCodeInput } from './OtpCodeInput'
import { authStore as authStoreFromMobile } from 'mobileApp/store'
import { authStore as authStoreFromAdmin } from 'adminConsole/store'

type OtpVerifyModalProps = {
  isOpen: boolean
  setIsVerifying: (isVerifying: boolean) => void
  setSubmitting: (isSubmitting: boolean) => void
  locateAt: CodeMapTokenWithLocate
}

export const OtpVerifyModal = ({ isOpen, setIsVerifying, setSubmitting, locateAt }: OtpVerifyModalProps) => {
  const navigate = useHistory()
  const authStore = locateAt === CodeMapTokenWithLocate.Admin ? authStoreFromAdmin : authStoreFromMobile
  const userInfo = authStore.currUser
  const { phone, email } = userInfo
  const defaultMethod = getOtpMethod(email, phone)

  return (
    <ModalWithReact
      isOpen={isOpen}
      title="兩階段身份驗證"
      onClose={() => {
        setIsVerifying(false)
        setSubmitting(false)
      }}>
      <h5 className="text-center text-muted font-weight-normal mb-4">請選擇驗證方式</h5>

      <Formik
        initialValues={{ verifyMethod: defaultMethod, otpCode: ''.padStart(6, '') }}
        onSubmit={(values, actions) => {
          authStore
            .verifyOtpCode({ type: values.verifyMethod, otpCode: values.otpCode })
            .then(() => {
              const { isRemindPasswordChange } = authStore.currUser

              authStore.pass2FA()
              navigate.push('/system/home')
              if (isRemindPasswordChange)
                toastr.warning('您的密碼即將到期，請盡快變更，逾期未變更將無法登入，需由首頁忘記密碼流程重新設定')
            })
            .catch(err => {
              actions.setFieldValue('otpCode', '')
              actions.setTouched({ otpCode: false })
              actions.setErrors({ otpCode: undefined })
              toastr.error(err)
            })
        }}>
        {(formikProps: FormikProps<{ otpCode: string; verifyMethod: OtpVerifyMethod }>) => (
          <OtpForm {...formikProps} phone={phone} email={email} locateAt={locateAt} />
        )}
      </Formik>
    </ModalWithReact>
  )
}

type OtpFormProps = FormikProps<{ otpCode: string; verifyMethod: OtpVerifyMethod }> & {
  phone: string
  email: string
  locateAt: CodeMapTokenWithLocate
}

const OtpForm = ({ handleSubmit, setFieldValue, phone, email, values, locateAt }: OtpFormProps) => {
  const authStore = locateAt === CodeMapTokenWithLocate.Admin ? authStoreFromAdmin : authStoreFromMobile
  const [isCounting, setIsCounting] = useState(false)
  const { otpCode, verifyMethod } = values
  const hasNoEmailOrPhone = verifyMethod === OtpVerifyMethod.Unknown

  const isDev = process.env.REACT_APP_ENV_TYPE !== 'production' && process.env.REACT_APP_ENV_TYPE !== 'uat'

  const handleGetOtp = () => {
    authStore.getOtpCode(verifyMethod).catch(err => toastr.error(err))
    if (isDev) setFieldValue('otpCode', '123456')
  }

  const countdownSeconds = useGetCodeMapCategorySystemValue({ reserve1: 'otp', locateAt })

  return (
    <>
      <form onSubmit={handleSubmit}>
        <CodeMapRadio
          category={CodeMapCategory.ListOtpVerifyMethod}
          value={verifyMethod}
          onChange={val => setFieldValue('verifyMethod', val)}
          optionDisabled={opt =>
            (opt.value === OtpVerifyMethod.SMS && !phone) ||
            (opt.value === OtpVerifyMethod.Email && !email) ||
            isCounting
          }
          locateAt={locateAt}
          className="d-flex justify-content-center mb-4"
        />

        <OtpCodeInput name="otpCode" />

        <div className="d-flex justify-content-around mt-4">
          <CountdownButton
            countdownSeconds={countdownSeconds}
            onGetOtpCode={handleGetOtp}
            isCounting={isCounting}
            setIsCounting={setIsCounting}
            disabled={hasNoEmailOrPhone}
          />

          <button type="submit" className="btn btn-primary" disabled={otpCode.length !== 6}>
            驗證
          </button>
        </div>
      </form>

      {isCounting && (
        <section className="text-center text-black-50 mt-4">
          <div>
            <span>已發送</span>
            <CodeMapDisplay category={CodeMapCategory.ListOtpVerifyMethod} value={verifyMethod} locateAt={locateAt} />
            <span>至</span>
            <span>【{verifyMethod === OtpVerifyMethod.Email ? maskSensitive(email) : maskSensitive(phone)}】</span>
            <span>，請於時限內輸入</span>
          </div>
          <span>{`若未收到，可於 ${countdownSeconds}`}</span>
          <span> 秒後重新取得驗證碼</span>
        </section>
      )}

      {hasNoEmailOrPhone && (
        <h6 className="text-center font-weight-normal mt-4 text-info">
          --此帳號無設定電子郵件與手機，無法取得 otp 驗證碼--
        </h6>
      )}
    </>
  )
}
